import { useBackgroundQuery, type QueryRef } from '@apollo/client/react';
import { useMemo } from 'react';

import { AssetContentTypeEnum, AssetsOrderByEnum, EscrowStatusEnum } from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import { createRequiredContext } from 'src/context/createRequiredContext';
import { useSoftwareEscrow } from 'src/context/software-escrow';

import type { IAssetsQuery, IAssetsQueryVariables } from 'src/graphql/actions/asset/assets.query';

const [useDepositsContext, DepositsContext] = createRequiredContext<{
  sourceCodeAssetsQueryRef: QueryRef<IAssetsQuery, IAssetsQueryVariables> | undefined;
  documentationAssetsQueryRef: QueryRef<IAssetsQuery, IAssetsQueryVariables> | undefined;
  dataAssetsQueryRef: QueryRef<IAssetsQuery, IAssetsQueryVariables> | undefined;
  unlabeledAssetsQueryRef: QueryRef<IAssetsQuery, IAssetsQueryVariables> | undefined;
}>();

const DepositsContextProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    softwareEscrow: { id: softwareEscrowId, status },
  } = useSoftwareEscrow();

  const isAssetsBlocked = status === EscrowStatusEnum.AdminReview || status === EscrowStatusEnum.Onboarding;
  const [sourceCodeAssetsQueryRef] = useBackgroundQuery<IAssetsQuery, IAssetsQueryVariables>(AssetsDocument, {
    variables: {
      softwareEscrowId,
      contentType: AssetContentTypeEnum.SourceCode,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: isAssetsBlocked,
  });
  const [documentationAssetsQueryRef] = useBackgroundQuery<IAssetsQuery, IAssetsQueryVariables>(AssetsDocument, {
    variables: {
      softwareEscrowId,
      contentType: AssetContentTypeEnum.Documentation,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: isAssetsBlocked,
  });
  const [dataAssetsQueryRef] = useBackgroundQuery<IAssetsQuery, IAssetsQueryVariables>(AssetsDocument, {
    variables: {
      softwareEscrowId,
      contentType: AssetContentTypeEnum.Data,
      perPage: 1,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: isAssetsBlocked,
  });
  const [unlabeledAssetsQueryRef] = useBackgroundQuery<IAssetsQuery, IAssetsQueryVariables>(AssetsDocument, {
    variables: {
      softwareEscrowId,
      perPage: 1,
      labeled: false,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: isAssetsBlocked,
  });

  const values = useMemo(
    () => ({
      sourceCodeAssetsQueryRef,
      documentationAssetsQueryRef,
      dataAssetsQueryRef,
      unlabeledAssetsQueryRef,
    }),
    [sourceCodeAssetsQueryRef, documentationAssetsQueryRef, dataAssetsQueryRef, unlabeledAssetsQueryRef],
  );

  return <DepositsContext value={values}>{children}</DepositsContext>;
};

export { useDepositsContext, DepositsContextProvider };
